"use client";

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Sharp geometric accents for the audience pages (2026-10-21, on instruction:
 * "new shapes and designs, sharp ones, to fill the gaps"). The prose sections
 * of `/developers`, `/enterprise`, `/about` and `/pk-endpoint` set their text
 * on the left and left the right of a wide screen empty. The heroes have
 * point-cloud solids instead (`three/poly-canvas.tsx`).
 *
 * Rules the art keeps, so it reads as part of the site rather than clip art:
 * - straight edges only (squares, diamonds, slabs, brackets), hairline strokes
 *   in the foreground ink, no colour, so it works in both themes;
 * - one solid element per piece, so the eye has somewhere to land;
 * - every shape is computed, never random, so the server and client render
 *   the same markup;
 * - it draws itself in once when scrolled into view, and simply appears for a
 *   reader who prefers reduced motion;
 * - it is decoration: `aria-hidden`, and hidden below `lg`, where the text
 *   already uses the full width.
 */

const STROKE = "stroke-current";
const DRAW = { duration: 1.1, ease: [0.22, 1, 0.36, 1] as const };

function Drawn({
  d,
  delay = 0,
  className,
  dashed = false,
}: {
  d: string;
  delay?: number;
  className?: string;
  dashed?: boolean;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.path
      d={d}
      fill="none"
      vectorEffect="non-scaling-stroke"
      strokeDasharray={dashed ? "3 5" : undefined}
      className={cn(STROKE, className)}
      initial={reduce || dashed ? false : { pathLength: 0, opacity: 0 }}
      whileInView={reduce || dashed ? undefined : { pathLength: 1, opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ ...DRAW, delay }}
    />
  );
}

function Appear({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.g
      initial={reduce ? false : { opacity: 0 }}
      whileInView={reduce ? undefined : { opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay }}
    >
      {children}
    </motion.g>
  );
}

/** Corner brackets framing a square box: the "this is an instrument" motif. */
function corners(x: number, y: number, s: number, len: number): string {
  const r = x + s;
  const b = y + s;
  return [
    `M${x} ${y + len}V${y}H${x + len}`,
    `M${r - len} ${y}H${r}V${y + len}`,
    `M${r} ${b - len}V${b}H${r - len}`,
    `M${x + len} ${b}H${x}V${b - len}`,
  ].join("");
}

/* ----------------------------------------------------------------- accents */

/** Small pieces for the right of a prose section; each is one idea. */
function Accent({ variant }: { variant: AccentVariant }) {
  switch (variant) {
    case "diamond":
      return (
        <>
          <Drawn d="M100 20L180 100L100 180L20 100Z" className="opacity-50" />
          <Drawn d="M100 56L144 100L100 144L56 100Z" className="opacity-35" delay={0.2} />
          <Appear delay={0.6}>
            <rect x={90} y={90} width={20} height={20} transform="rotate(45 100 100)" className="fill-current opacity-90" />
          </Appear>
        </>
      );
    case "steps":
      return (
        <>
          {[0, 1, 2, 3, 4].map((i) => {
            const h = 28 + i * 30;
            return (
              <Appear key={i} delay={0.1 + i * 0.12}>
                <rect
                  x={24 + i * 32}
                  y={176 - h}
                  width={22}
                  height={h}
                  vectorEffect="non-scaling-stroke"
                  className={cn(STROKE, i === 4 ? "fill-current opacity-90" : "fill-none opacity-45")}
                />
              </Appear>
            );
          })}
          <Drawn d="M16 182H186" className="opacity-40" delay={0.1} />
        </>
      );
    case "brackets":
      return (
        <>
          <Drawn d={corners(24, 24, 152, 34)} className="opacity-55" />
          <Drawn d="M72 76L48 100L72 124M128 76L152 100L128 124" className="opacity-60" delay={0.3} />
          <Appear delay={0.7}>
            <rect x={92} y={92} width={16} height={16} className="fill-current opacity-90" />
          </Appear>
        </>
      );
    case "wedge":
      return (
        <>
          <Drawn d="M20 180L100 24L180 180Z" className="opacity-50" />
          <Drawn d="M60 180L100 102L140 180" className="opacity-35" delay={0.2} />
          <Appear delay={0.6}>
            <path d="M88 180L100 156L112 180Z" className="fill-current opacity-90" />
          </Appear>
        </>
      );
    case "grid":
      return (
        <>
          {[0, 1, 2].flatMap((r) =>
            [0, 1, 2].map((c) => (
              <Appear key={`${r}-${c}`} delay={0.1 + (r + c) * 0.1}>
                <rect
                  x={34 + c * 46}
                  y={34 + r * 46}
                  width={38}
                  height={38}
                  vectorEffect="non-scaling-stroke"
                  className={cn(STROKE, r === 1 && c === 2 ? "fill-current opacity-90" : "fill-none opacity-40")}
                />
              </Appear>
            )),
          )}
        </>
      );
    case "chevrons":
      return (
        <>
          {[0, 1, 2].map((i) => (
            <Drawn key={i} d={`M${36 + i * 44} 52L${84 + i * 44} 100L${36 + i * 44} 148`} delay={i * 0.18} className={i === 2 ? "opacity-90" : "opacity-40"} />
          ))}
        </>
      );
    case "crosshair":
      return (
        <>
          <Drawn d="M100 14V70M100 130V186M14 100H70M130 100H186" className="opacity-45" />
          <Drawn d={corners(56, 56, 88, 18)} className="opacity-60" delay={0.2} />
          <Appear delay={0.6}>
            <rect x={92} y={92} width={16} height={16} className="fill-current opacity-90" />
          </Appear>
        </>
      );
  }
}

export type AccentVariant = "diamond" | "steps" | "brackets" | "wedge" | "grid" | "chevrons" | "crosshair";

export function GeoAccent({ variant, className }: { variant: AccentVariant; className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      aria-hidden="true"
      focusable="false"
      className={cn("h-auto w-full overflow-visible text-foreground", className)}
      strokeWidth={1}
    >
      <Accent variant={variant} />
    </svg>
  );
}
